import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../config/firebase";
import {
  applyAppearanceToDom,
  readAppearanceMirror,
  writeAppearanceMirror,
} from "./appearanceStorage";

const ThemeContext = createContext(null);

function initialAppearance() {
  const mirror = readAppearanceMirror();
  return mirror === "dark" ? "dark" : "light";
}

export function ThemeProvider({ children }) {
  const [appearance, setAppearanceState] = useState(initialAppearance);
  const [prefsLoading, setPrefsLoading] = useState(true);

  useLayoutEffect(() => {
    applyAppearanceToDom(appearance);
  }, [appearance]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        const mirror = readAppearanceMirror();
        setAppearanceState(mirror === "dark" ? "dark" : "light");
        setPrefsLoading(false);
        return;
      }

      setPrefsLoading(true);
      try {
        const snap = await getDoc(
          doc(db, "userPreferences", firebaseUser.uid),
        );
        if (snap.exists()) {
          const a = snap.data()?.appearance;
          if (a === "dark" || a === "light") {
            setAppearanceState(a);
            writeAppearanceMirror(a);
          } else {
            setAppearanceState("light");
          }
        } else {
          setAppearanceState("light");
        }
      } catch (err) {
        console.error("[ThemeProvider] userPreferences read failed:", err);
        setAppearanceState("light");
      } finally {
        setPrefsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const setAppearance = useCallback(async (next) => {
    if (next !== "light" && next !== "dark") return;

    const firebaseUser = auth.currentUser;
    const prev = appearance;

    if (!firebaseUser) {
      setAppearanceState(next);
      writeAppearanceMirror(next);
      return;
    }

    setAppearanceState(next);
    try {
      await setDoc(
        doc(db, "userPreferences", firebaseUser.uid),
        {
          appearance: next,
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );
      writeAppearanceMirror(next);
    } catch (err) {
      console.error("[ThemeProvider] userPreferences write failed:", err);
      setAppearanceState(prev);
    }
  }, [appearance]);

  const value = useMemo(
    () => ({
      appearance,
      prefsLoading,
      setAppearance,
    }),
    [appearance, prefsLoading, setAppearance],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (ctx == null) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return ctx;
}
